import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { FaFileInvoiceDollar, FaLock } from "react-icons/fa";
import Loader from "../components/Loader";
import {
  useGetOrderDetailsQuery,
  usePayOrderMutation,
  useCompleteOrderPurchaseMutation,
} from "../slices/ordersApiSlice";
import { useSelector } from "react-redux";
import { useGetExchangeRatesQuery } from "../slices/exchangeSlice";
import { formatPrice } from "../utils/formatPrice";
import {
  useCreateStripePaymentIntentMutation,
  useGetStripeConfigQuery,
} from "../slices/stripeApiSlice";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { trackEvent } from "../lib/analytics";

const StripePayForm = ({ order, onPaid, createStripePaymentIntent }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [cardholderName, setCardholderName] = useState("");

  const submitHandler = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    const card = elements.getElement(CardNumberElement);
    if (!card) {
      toast.error("Card form is not ready yet.");
      return;
    }

    try {
      setProcessing(true);
      const intent = await createStripePaymentIntent(order._id).unwrap();
      const { error, paymentIntent } = await stripe.confirmCardPayment(intent.clientSecret, {
        payment_method: {
          card,
          billing_details: {
            name: cardholderName || order?.user?.name || "",
            email: order?.user?.email || "",
          },
        },
      });

      if (error) {
        throw new Error(error.message || "Stripe payment failed");
      }
      if (!paymentIntent || paymentIntent.status !== "succeeded") {
        throw new Error("Stripe payment did not complete successfully");
      }

      await onPaid(paymentIntent);
      toast.success("Payment successful!");
    } catch (err) {
      toast.error(err?.data?.message || err?.message || "Stripe payment failed");
    } finally {
      setProcessing(false);
    }
  };

  const stripeFieldOptions = {
    style: {
      base: {
        fontSize: "16px",
        color: "#1e293b",
        "::placeholder": {
          color: "#94a3b8",
        },
      },
      invalid: {
        color: "#dc2626",
      },
    },
  };

  const elementBaseClass =
    "rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-800";

  return (
    <form onSubmit={submitHandler} className="space-y-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <div>
        <h3 className="text-xl font-semibold text-slate-900">Payment Method</h3>
        <p className="mt-1 text-sm text-slate-600">All transactions are secure and encrypted</p>
      </div>
      <div>
        <label className="mb-1.5 block text-sm font-medium text-slate-700">Name on Card</label>
        <input
          type="text"
          value={cardholderName}
          onChange={(e) => setCardholderName(e.target.value)}
          placeholder={order?.user?.name || "John Doe"}
          className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
        />
      </div>
      <div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-700">Card Number</label>
          <div className={elementBaseClass}>
            <CardNumberElement
              options={{ ...stripeFieldOptions, placeholder: "1234 5678 9012 3456" }}
            />
          </div>
        </div>
        <p className="mt-1 text-xs text-slate-600">Enter your 16-digit number.</p>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-700">Month / Year</label>
          <div className={elementBaseClass}>
            <CardExpiryElement options={{ ...stripeFieldOptions, placeholder: "MM / YY" }} />
          </div>
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-700">CVV</label>
          <div className={elementBaseClass}>
            <CardCvcElement options={{ ...stripeFieldOptions, placeholder: "123" }} />
          </div>
        </div>
      </div>
      <button
        type="submit"
        disabled={!stripe || processing}
        className="inline-flex w-full items-center justify-center rounded-xl bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:bg-slate-300"
      >
        <FaLock className="mr-2" />
        {processing ? "Processing..." : "Pay with Stripe"}
      </button>
      <p className="text-xs leading-5 text-slate-500">
        Secure checkout is processed through Stripe. If you experience a payment issue, contact{" "}
        <a
          href="mailto:contact@practidex.com"
          className="font-semibold text-sky-700 no-underline hover:text-sky-800"
        >
          contact@practidex.com
        </a>
        .
      </p>
    </form>
  );
};

function OrderScreen() {
  const { id: orderId } = useParams();

  const {
    data: order,
    refetch,
    isLoading,
    error,
  } = useGetOrderDetailsQuery({ orderId, includeCreators: true });

  const [payOrder] = usePayOrderMutation();
  const [completeOrderPurchase] = useCompleteOrderPurchaseMutation(orderId);
  const [createStripePaymentIntent] = useCreateStripePaymentIntentMutation();
  const { data: stripeConfig } = useGetStripeConfigQuery();

  const userInfo = useSelector((state) => state.auth.userInfo);
  const preferredCurrency = userInfo?.preferredCurrency || "USD";
  const { data: exchangeData } = useGetExchangeRatesQuery();
  const stripePromise = useMemo(() => {
    if (!stripeConfig?.publishableKey) return null;
    return loadStripe(stripeConfig.publishableKey);
  }, [stripeConfig?.publishableKey]);

  const onStripePaid = async (paymentIntent) => {
    await payOrder({
      orderId,
      details: { transactionId: paymentIntent.id },
    }).unwrap();
    await completeOrderPurchase(orderId).unwrap();
    trackEvent("purchase_completed", {
      order_id: orderId,
      payment_method: "stripe",
      value: Number(order?.chargeableTotal ?? order?.totalPrice ?? 0),
    });
    refetch();
  };

  const onClaimFreeOrder = async () => {
    try {
      await payOrder({
        orderId,
        details: {},
      }).unwrap();
      trackEvent("purchase_completed", {
        order_id: orderId,
        payment_method: "free",
        value: 0,
      });
      toast.success("Free order claimed successfully!");
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || err?.message || "Failed to claim free order");
    }
  };

  if (isLoading) return <Loader />;
  if (error) {
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700">
        Failed to load order details.
      </div>
    );
  }

  const isLegacyPaypalOrder = order?.paymentMethod === "PayPal";
  const isFreeOnlyOrder = Number(order?.chargeableTotal || 0) <= 0;
  const freeItemsCount = (order?.orderItems || [])
    .filter((item) => Boolean(item.isFree) || Number(item.price) <= 0)
    .reduce((acc, item) => acc + Number(item.qty || 0), 0);
  const chargeableTotal = Number(order?.chargeableTotal ?? order?.totalPrice ?? 0);

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-700">Checkout</p>
        <h1 className="text-2xl font-semibold text-slate-900">Order {order._id}</h1>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <section className="space-y-5 lg:col-span-7">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-3 flex items-center justify-between gap-3">
              <h2 className="text-xl font-semibold text-slate-900">Shipping</h2>
              <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                Buyer Details
              </div>
            </div>
            <div className="space-y-1 text-sm text-slate-700">
              <p className="m-0">
                <strong>Name:</strong> {order.user.name}
              </p>
              <p className="m-0">
                <strong>Email:</strong> {order.user.email}
              </p>
              <p className="m-0">
                <strong>Address:</strong> {order.shippingAddress.address}, {order.shippingAddress.city}{" "}
                {order.shippingAddress.postalCode}, {order.shippingAddress.country}
              </p>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-3 flex items-center justify-between gap-3">
              <h2 className="text-xl font-semibold text-slate-900">Payment Method</h2>
              <div
                className={`rounded-full px-3 py-1 text-xs font-semibold ${
                  order.isPaid
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-rose-100 text-rose-700"
                }`}
              >
                {order.isPaid ? "Paid" : "Awaiting payment"}
              </div>
            </div>
            <p className="m-0 text-sm text-slate-700">
              <strong>Method:</strong> {order.paymentMethod}
            </p>
            <div
              className={`mt-3 rounded-xl px-3 py-2 text-sm font-medium ${
                order.isPaid
                  ? "border border-emerald-200 bg-emerald-50 text-emerald-700"
                  : "border border-rose-200 bg-rose-50 text-rose-700"
              }`}
            >
              {order.isPaid ? `Paid on ${order.paidAt}` : "Not Paid"}
            </div>
            <p className="mt-3 text-sm leading-6 text-slate-500">
              {order.isPaid
                ? "Your payment has been recorded and the purchase is available in your account."
                : isFreeOnlyOrder
                ? "Free-only orders do not require Stripe. Claiming the order will add the resources to your library immediately."
                : "Once payment succeeds, this order is added to your purchase history and ready for download."}
            </p>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between gap-3">
              <h2 className="text-xl font-semibold text-slate-900">Order Items</h2>
              <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                {order.orderItems.length} item{order.orderItems.length === 1 ? "" : "s"}
              </div>
            </div>
            <div className="space-y-3">
              {order.orderItems.map((item, index) => (
                <article
                  key={index}
                  className="grid grid-cols-1 items-center gap-4 rounded-xl border border-slate-200 bg-slate-50 p-3 sm:grid-cols-[56px_minmax(0,1fr)_220px]"
                >
                  <img
                    src={item.images[0]}
                    alt={item.name}
                    className="h-14 w-14 rounded-md border border-slate-200 object-cover"
                  />
                  <Link
                    to={`/product/${item.product}`}
                    className="truncate text-sm font-semibold text-slate-800 no-underline hover:text-sky-700"
                  >
                    {item.name}
                  </Link>
                  <div className="text-sm text-slate-700">
                    {item.qty} x{" "}
                    {Boolean(item.isFree) || Number(item.price) <= 0
                      ? "Free"
                      : formatPrice(item.price, preferredCurrency, exchangeData)}{" "}
                    ={" "}
                    <strong>
                      {Boolean(item.isFree) || Number(item.price) <= 0
                        ? "Free"
                        : formatPrice(item.price * item.qty, preferredCurrency, exchangeData)}
                    </strong>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <aside className="lg:col-span-5">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 space-y-1">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-700">
                Review
              </p>
              <h2 className="text-xl font-semibold text-slate-900">Order Summary</h2>
            </div>
            <div className="space-y-2 text-sm text-slate-700">
              <div className="flex items-center justify-between gap-4">
                <span>Total:</span>
                <strong>
                  {chargeableTotal <= 0
                    ? "Free"
                    : formatPrice(chargeableTotal, preferredCurrency, exchangeData)}
                </strong>
              </div>
            </div>

            <div className="mt-4 border-t border-slate-200 pt-4 text-xs leading-5 text-slate-500">
              {freeItemsCount > 0 && (
                <p className="mb-2">
                  Includes {freeItemsCount} free item{freeItemsCount === 1 ? "" : "s"} in this order.
                </p>
              )}
              {order.isPaid
                ? "Your order is complete. You can review the invoice below at any time."
                : isFreeOnlyOrder
                ? "This order only contains free items. Claiming it will add the resources to your library."
                : "Complete payment securely with Stripe to unlock the purchased resources."}
            </div>
            {order.isPaid && (
              <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm leading-6 text-emerald-800">
                Payment successful. You can now head to{" "}
                <Link
                  to="/purchases"
                  className="font-semibold text-emerald-900 underline underline-offset-2"
                >
                  Purchase History
                </Link>{" "}
                to download your product files whenever you need them.
              </div>
            )}

            {order.isPaid && (
              <div className="mt-4">
                <Link
                  to={`/order/${orderId}/invoice`}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 no-underline transition hover:bg-slate-50"
                >
                  <FaFileInvoiceDollar />
                  View Invoice
                </Link>
              </div>
            )}

            {order.isPaid === false && (
              <div className="mt-5">
                {isFreeOnlyOrder ? (
                  <div className="space-y-3">
                    <div className="rounded-xl border border-sky-200 bg-sky-50 px-3 py-2 text-sm font-medium text-sky-700">
                      This order only contains free items.
                    </div>
                    <button
                      type="button"
                      onClick={onClaimFreeOrder}
                      className="inline-flex w-full items-center justify-center rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700"
                    >
                      Get Now
                    </button>
                  </div>
                ) : isLegacyPaypalOrder ? (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-700">
                    This unpaid order uses legacy PayPal checkout, which is disabled in the current
                    frontend.
                  </div>
                ) : stripePromise ? (
                  <Elements stripe={stripePromise}>
                    <StripePayForm
                      order={order}
                      onPaid={onStripePaid}
                      createStripePaymentIntent={createStripePaymentIntent}
                    />
                  </Elements>
                ) : (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-700">
                    Stripe is not configured for this environment.
                  </div>
                )}
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

export default OrderScreen;

